import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const BASE =
  "/api/contractor-onboarding/public/onboarding";

/*
 * IRIS V1.0C-H-R9-B
 *
 * The parent ContractorOnboardingInvitation page is the
 * sole owner of the active candidate session.
 *
 * This component receives that canonical session token
 * through props and binds it to the acknowledgement API
 * using X-Onboarding-Session.
 */

async function acknowledgementRequest(
  path,
  sessionToken,
  body
) {
  if (!sessionToken) {
    const error = new Error(
      "The secure onboarding session is unavailable."
    );

    error.status = 401;
    throw error;
  }

  const headers = {
    "X-Onboarding-Session":
      sessionToken,
  };

  if (body !== undefined) {
    headers["Content-Type"] =
      "application/json";
  }

  const response = await fetch(
    `${BASE}${path}`,
    {
      method: "POST",
      headers,
      body:
        body !== undefined
          ? JSON.stringify(body)
          : undefined,
      credentials: "same-origin",
    }
  );

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      data?.detail ||
      "The acknowledgement request failed."
    );

    error.status = response.status;

    throw error;
  }

  return data;
}


export default function
SubcontractorAcknowledgementGate({
  sessionToken,
  currentStep,
  onExecuted,
}) {
  const [status, setStatus] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [accepted, setAccepted] =
    useState(false);

  const [signature, setSignature] =
    useState("");

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  /*
   * V1.0C-I-R6-D-R4-R3
   *
   * Prevent repeated resume-to-review transitions when an
   * already-executed acknowledgement is loaded on refresh.
   */
  const resumeTransitionFiredRef =
    useRef(false);

  const relevantStep =
    currentStep === "declarations" ||
    currentStep === "review";

  const refresh =
    useCallback(
      async () => {
        if (
          !sessionToken ||
          !relevantStep
        ) {
          return;
        }

        setLoading(true);
        setError("");

        try {
          const result =
            await acknowledgementRequest(
              "/acknowledgement/status",
              sessionToken
            );

          setStatus(result);

          if (
            result?.candidate_legal_name
          ) {
            setSignature(
              current =>
                current ||
                result.candidate_legal_name
            );
          }

        } catch (requestError) {
          setStatus(null);

          setError(
            requestError?.message ||
            "The acknowledgement could not be loaded."
          );

        } finally {
          setLoading(false);
        }
      },
      [
        sessionToken,
        relevantStep,
      ]
    );

  useEffect(
    () => {
      if (
        !relevantStep ||
        !sessionToken
      ) {
        return;
      }

      refresh();
    },
    [
      refresh,
      relevantStep,
      sessionToken,
    ]
  );

  /*
   * Rapid X migration:
   *
   * The authoritative acknowledgement service returns employer
   * identity inside the immutable document projection.
   */
  const employerName =
    status?.document?.employer_name ||
    status?.employer_legal_name ||
    status?.contractor_name ||
    "";

  const employerAbn =
    status?.document?.employer_abn ||
    status?.employer_abn ||
    "";

  const principalName =
    status?.document?.principal_name ||
    "BSMS Security Pty Ltd";

  const candidateName =
    status?.candidate_legal_name ||
    signature.trim() ||
    "";

  const executed =
    Boolean(status?.executed);

  /*
   * IRIS_R4_R3_EXECUTED_RESUME
   *
   * If a candidate reloads while the backend still has the
   * draft at declarations but the acknowledgement is already
   * executed, resume the canonical workflow exactly once by
   * asking the parent to open the review projection.
   *
   * Do not execute the acknowledgement again.
   */
  /*
   * IRIS_R4_R3_GUARD_RESET
   *
   * If this component is reused for an unsigned declarations
   * step, allow a future successful execution to transition.
   */
  useEffect(
    () => {
      if (!executed) {
        resumeTransitionFiredRef.current = false;
      }
    },
    [
      executed,
      sessionToken,
    ]
  );

  useEffect(
    () => {
      if (
        currentStep !== "declarations" ||
        !executed ||
        resumeTransitionFiredRef.current ||
        typeof onExecuted !== "function"
      ) {
        return;
      }

      resumeTransitionFiredRef.current = true;

      Promise.resolve(
        onExecuted()
      ).catch(
        transitionError => {
          resumeTransitionFiredRef.current = false;

          setError(
            transitionError?.message ||
            "Your acknowledgement is signed, but the review step could not be opened."
          );
        }
      );
    },
    [
      currentStep,
      executed,
      onExecuted,
    ]
  );

  const documentVersion =
    status?.document?.version ||
    status?.document_version ||
    "BSMS-SUBEMP-ACK-V1.0";

  const card = useMemo(
    () => ({
      background: "#ffffff",
      border: "1px solid #d1d5db",
      borderRadius: "14px",
      padding: "24px",
      marginBottom: "18px",
      boxShadow:
        "0 3px 15px rgba(0,0,0,.06)",
    }),
    []
  );

  if (!relevantStep) {
    return null;
  }

  async function execute() {
    if (!accepted) {
      setError(
        "You must confirm that you have read and understood the acknowledgement."
      );

      return;
    }

    if (!signature.trim()) {
      setError(
        "Please enter your full legal name as your electronic signature."
      );

      return;
    }

    setBusy(true);
    setError("");

    try {
      await acknowledgementRequest(
        "/acknowledgement/execute",
        sessionToken,
        {
          accepted: true,
          signature_name:
            signature.trim(),
        }
      );

      await refresh();

      /*
       * V1.0C-I-R6-D-R4-R2
       *
       * Execution has succeeded authoritatively.
       * Inform the parent workflow so it can open
       * the backend Review projection immediately.
       */
      if (typeof onExecuted === "function") {
        await onExecuted();
      }

    } catch (requestError) {
      setError(
        requestError?.message ||
        "Acknowledgement could not be signed."
      );

    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={card}
      aria-label={
        "Subcontractor Employee Acknowledgement and Indemnity"
      }
    >
      <div
        style={{
          fontSize: "12px",
          letterSpacing: "1px",
          textTransform:
            "uppercase",
          color: "#64748b",
          fontWeight: 800,
          marginBottom: "6px",
        }}
      >
        Required before final submission
      </div>

      <h2
        style={{
          margin: "0 0 8px",
          fontSize: "22px",
          color: "#111827",
        }}
      >
        Subcontractor Employee
        Acknowledgement and Indemnity
      </h2>

      <div
        style={{
          color: "#64748b",
          fontSize: "12px",
          marginBottom: "20px",
        }}
      >
        Document version:{" "}
        {documentVersion}
      </div>

      {loading && (
        <div
          style={{
            padding: "14px",
            background: "#f8fafc",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          Loading acknowledgement...
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "14px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>
            Acknowledgement action required:
          </strong>{" "}
          {error}
        </div>
      )}

      <div
        style={{
          lineHeight: 1.65,
          color: "#1f2937",
          fontSize: "14px",
        }}
      >
        <p>
          I acknowledge and agree that I am
          employed solely by{" "}
          <strong>
            {employerName}
          </strong>{" "}
          ("Employer") and that my employment
          relationship exists exclusively with
          my Employer.
        </p>

        <p>
          I further acknowledge that{" "}
          <strong>
            {principalName}
          </strong>{" "}
          ("BSMS") has engaged my Employer to
          provide security personnel under a
          subcontract arrangement, with BSMS
          acting as the principal contractor to
          its client.
        </p>

        <p>
          I acknowledge and agree that:
        </p>

        <ol
          style={{
            paddingLeft: "22px",
          }}
        >
          <li style={{ marginBottom: "10px" }}>
            BSMS is <strong>not</strong> my
            employer and has not offered or
            entered into any contract of
            employment with me.
          </li>

          <li style={{ marginBottom: "10px" }}>
            My Employer is solely responsible
            for all employment-related
            obligations owed to me, including
            but not limited to:

            <ul style={{ marginTop: "8px" }}>
              <li>
                payment of wages, overtime,
                allowances and penalty rates;
              </li>
              <li>leave entitlements;</li>
              <li>
                superannuation contributions;
              </li>
              <li>taxation obligations;</li>
              <li>
                workers' compensation insurance;
              </li>
              <li>
                industrial relations compliance;
              </li>
              <li>payroll obligations;</li>
              <li>employment benefits; and</li>
              <li>
                all other statutory,
                contractual and award
                obligations arising from my
                employment.
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "10px" }}>
            Any questions, disputes, claims or
            concerns relating to my employment,
            remuneration or employment
            entitlements must be raised directly
            with my Employer and not with BSMS.
          </li>

          <li style={{ marginBottom: "10px" }}>
            My attendance on any BSMS-managed
            project or client site does not
            create an employment relationship
            between myself and BSMS.
          </li>

          <li style={{ marginBottom: "10px" }}>
            To the maximum extent permitted by
            law, I release and indemnify BSMS,
            its directors, officers, employees
            and agents from any claim, demand,
            loss, liability, cost or expense
            arising out of or in connection with
            any employment-related entitlement
            or obligation owed to me by my
            Employer, including claims relating
            to wages, leave, superannuation,
            industrial instruments, payroll,
            taxation or other employment
            benefits.
          </li>

          <li style={{ marginBottom: "10px" }}>
            This indemnity does not apply to the
            extent that any liability arises
            directly from BSMS's own negligence,
            breach of contract, contravention of
            legislation, or any obligation that
            cannot lawfully be excluded under
            applicable law.
          </li>

          <li style={{ marginBottom: "10px" }}>
            I acknowledge that I have had the
            opportunity to obtain independent
            legal advice before signing this
            acknowledgement and that I
            understand its effect.
          </li>
        </ol>

        <p
          style={{
            fontWeight: 700,
            marginTop: "18px",
          }}
        >
          Nothing in this acknowledgement limits
          or excludes any rights or obligations
          that cannot be excluded under the
          Fair Work Act 2009 (Cth), applicable
          work health and safety legislation,
          workers' compensation legislation, or
          any other applicable law.
        </p>
      </div>

      <div
        style={{
          borderTop:
            "1px solid #e5e7eb",
          marginTop: "22px",
          paddingTop: "20px",
        }}
      >
        <div style={{ marginBottom: "10px" }}>
          <strong>Employee:</strong>{" "}
          {candidateName || "Candidate"}
        </div>

        <div style={{ marginBottom: "18px" }}>
          <strong>
            Employer (Subcontractor):
          </strong>{" "}
          {employerName} — ABN{" "}
          {employerAbn}
        </div>

        {executed ? (
          <div
            style={{
              padding: "15px",
              background: "#ecfdf5",
              border: "1px solid #a7f3d0",
              color: "#065f46",
              borderRadius: "8px",
              fontWeight: 700,
            }}
          >
            Acknowledgement signed
            successfully
            {status?.signature_name
              ? ` by ${status.signature_name}`
              : ""}
            .
          </div>
        ) : (
          <>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                cursor: "pointer",
                marginBottom: "18px",
              }}
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={
                  event =>
                    setAccepted(
                      event.target.checked
                    )
                }
                style={{
                  marginTop: "4px",
                }}
              />

              <span>
                I confirm that I have read and
                understood the Subcontractor
                Employee Acknowledgement and
                Indemnity above and agree to be
                bound by its terms.
              </span>
            </label>

            <label
              style={{
                display: "block",
                fontWeight: 700,
                marginBottom: "6px",
              }}
            >
              Electronic signature — full legal
              name
            </label>

            <input
              className="rapidx-onboarding-signature-input"
              type="text"
              value={signature}
              onChange={
                event =>
                  setSignature(
                    event.target.value
                  )
              }
              placeholder={
                candidateName ||
                "Enter your full legal name"
              }
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "12px",
                border: "1px solid #94a3b8",
                borderRadius: "8px",
                marginBottom: "14px",
                fontSize: "14px",
                background: "#ffffff",
                color: "#111827",
                WebkitTextFillColor: "#111827",
                caretColor: "#111827",
                opacity: 1,
              }}
            />

            <button
              type="button"
              disabled={
                busy ||
                loading ||
                !sessionToken
              }
              onClick={execute}
              style={{
                width: "100%",
                padding: "13px",
                border: 0,
                borderRadius: "8px",
                background: "#0f172a",
                color: "#fff",
                fontWeight: 800,
                cursor:
                  busy
                    ? "default"
                    : "pointer",
                opacity:
                  busy ||
                  loading ||
                  !sessionToken
                    ? 0.6
                    : 1,
              }}
            >
              {busy
                ? "Signing..."
                : "Sign acknowledgement"}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
